import React, { useState, useEffect } from 'react';
import { ComingSoonReport as ComingSoonReportType } from '@/api/coming-soon';

import CopyIcon from '@/assets/copy-simple-light.svg';

interface ComingSoonReportProps {
  report: ComingSoonReportType;
  onSubscriptionChange: (reportId: string, isSubscribed: boolean) => void;
}

const ComingSoonReport: React.FC<ComingSoonReportProps> = ({ report, onSubscriptionChange }) => {
  const [isSubscribed, setIsSubscribed] = useState(Boolean(report.isSubscribed));

  useEffect(() => {
    setIsSubscribed(Boolean(report.isSubscribed));
  }, [report.isSubscribed]);

  const handleSubscriptionToggle = () => {
    const newState = !isSubscribed;
    setIsSubscribed(newState);
    onSubscriptionChange(report.reportId, newState);
  };

  const copyToClipboard = async () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = report.text || '';
    const plainText = tempDiv.innerText || tempDiv.textContent;
    const formattedContent = `${report.title}\n${plainText}\n`;

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(formattedContent);
      }
    } catch {
      // Silent fail
    }
  };

  const pubDate = new Date(report.publishDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="border-b border-gray-200">
      <div className="p-3 sm:p-4">
        <div className="mb-2 flex flex-col items-start justify-between gap-1 sm:flex-row sm:items-center sm:gap-0">
          <div className="ml-12 text-[12px] leading-[18px] font-bold text-black sm:ml-14">
            {report.type} | {report.directory}
          </div>
          <div className="ml-12 text-[12px] leading-[18px] font-bold text-black sm:ml-0">{pubDate}</div>
        </div>

        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-start sm:gap-4">
          <div className="flex w-full items-start gap-2 sm:w-auto sm:gap-4">
            <label className="mt-[18px] flex flex-1 cursor-pointer items-center sm:flex-initial">
              <input type="checkbox" checked={isSubscribed} onChange={handleSubscriptionToggle} className="sr-only" />
              <div
                className={`h-6 w-12 flex-shrink-0 rounded-full transition-colors ${isSubscribed ? 'bg-black' : 'bg-gray-300'}`}
              >
                <div
                  className={`h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                    isSubscribed ? 'translate-x-6' : 'translate-x-0.5'
                  } mt-0.5`}
                />
              </div>
              <span className="ml-2 text-[16px] leading-[20px] font-bold text-black sm:text-[18px] sm:leading-[22px]">
                {report.title}
              </span>
            </label>

            <div className="mb-1 pb-2">
              <button
                onClick={copyToClipboard}
                className="flex-shrink-0 cursor-pointer ml-5 w-[71px] h-10 mt-2.5 px-5 rounded-[25px] hover:bg-gray-100"
              >
                <CopyIcon className="h-6 w-6 sm:h-7 sm:w-7" />
              </button>
            </div>
          </div>
        </div>

        {report.text && (
          <div
            className="mt-2 ml-12 text-[14px] leading-[20px] text-black sm:ml-14 sm:text-[16px] sm:leading-[22px]"
            dangerouslySetInnerHTML={{ __html: report.text }}
          />
        )}
      </div>
    </div>
  );
};

export default ComingSoonReport;
